import { AfterViewInit, Directive, ElementRef, Inject, Input, OnDestroy } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { AkamaiService } from '@shared/services/akamai.service';

@Directive({
    selector: '[lazyPieceImage]',
})
export class LazyPieceImageDirective implements AfterViewInit, OnDestroy {
    @Input() private readonly lazyPieceImage: string;

    @Input() private readonly imageSkeleton: HTMLDivElement;

    private intersectionObserver: IntersectionObserver;

    private readonly offsetWidth$$ = new Subject<number>();

    private readonly offsetWidth$ = this.offsetWidth$$.asObservable();

    private readonly onDestroy$ = new Subject<void>();

    constructor(
        private readonly elementRef: ElementRef,
        @Inject(WINDOW) private readonly window: CommonWindow
    ) { }

    public ngAfterViewInit() {
        this.listenToVisibilityChanges().then();
    }

    @BrowserOnly<LazyPieceImageDirective, Promise<void>>(Promise.resolve())
    private async listenToVisibilityChanges() {
        const img = await this.elementRef.nativeElement;
        this.offsetWidth$.pipe(
            distinctUntilChanged(),
            filter((offsetWidth) => {
                return offsetWidth > 0;
            }),
            takeUntil(this.onDestroy$)
        ).subscribe((_) => {
            this.createIntersectionObserver(img);
        });

        this.offsetWidth$$.next(img.offsetWidth);
        const observer = new MutationObserver(() => {
            this.offsetWidth$$.next(img.offsetWidth);
        });
        observer.observe(img, { attributes: true, childList: true, subtree: true });
    }

    private createIntersectionObserver(img: HTMLImageElement) {
        this.intersectionObserver = new IntersectionObserver(entries => {
            this.checkForIntersection(entries, img);
        });
        this.intersectionObserver.observe(img);
    }

    private checkForIntersection(entries: IntersectionObserverEntry[], img: HTMLImageElement) {
        entries.forEach((entry: IntersectionObserverEntry) => {
            if (this.checkIfIntersecting(entry, img)) {
                this.setImageUrl(img);
                this.intersectionObserver.unobserve(img);
                this.intersectionObserver.disconnect();
            }
        });
    }

    private setImageUrl(img: HTMLImageElement) {
        const { offsetWidth } = img;
        const url = new URL(this.lazyPieceImage);
        const impolicy = url.searchParams.get('impolicy');
        const imageUrl = AkamaiService.useAkamaiImage(this.window, this.lazyPieceImage, offsetWidth, impolicy);
        img.setAttribute('src', imageUrl.toString());
        // eslint-disable-next-line no-param-reassign
        img.onload = () => {
            this.imageSkeleton.classList.add('hide-skeleton');
        };
    }

    private checkIfIntersecting(entry: IntersectionObserverEntry, img: HTMLImageElement) {
        return entry.isIntersecting && entry.target === img;
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
